import { CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import type { LucideIcon } from "lucide-react";
import type { FC } from "react";

const ICON_SIZE = 18;

export type DrawerAction = {
	key: string;
	label: string;
	icon: LucideIcon;
	onClick: () => void;
	isDestructive?: boolean;
	isPending?: boolean;
	disabled?: boolean;
};

type Props = {
	action: DrawerAction;
	isLast: boolean;
};

export const ActionsDrawerRow: FC<Props> = ({ action, isLast }) => {
	const theme = useTheme();

	const {
		label,
		icon: Icon,
		onClick,
		isDestructive,
		isPending,
		disabled,
	} = action;
	const isDisabled = disabled === true || isPending === true;

	const contentColor = isDisabled
		? theme.palette.grey[400]
		: isDestructive
			? theme.palette.red[500]
			: undefined;

	const iconColor = isDisabled
		? theme.palette.grey[300]
		: isDestructive
			? theme.palette.red[500]
			: theme.palette.grey[500];

	return (
		<Stack
			direction="row"
			alignItems="center"
			gap={theme.spacingTokens.md}
			px={theme.spacingTokens.lg}
			py={theme.spacingTokens.md}
			borderBottom={
				isLast ? undefined : `1px solid ${theme.palette.surface.border}`
			}
			onClick={(event) => {
				event.stopPropagation();
				if (isDisabled) {
					return;
				}
				onClick();
			}}
			sx={{
				cursor: isDisabled ? "default" : "pointer",
				"&:active": {
					backgroundColor: isDisabled ? undefined : theme.palette.grey[100],
				},
			}}
		>
			{isPending ? (
				<CircularProgress size={ICON_SIZE} />
			) : (
				<Icon size={ICON_SIZE} color={iconColor} />
			)}
			<Typography variant="bodyMedium" color={contentColor}>
				{label}
			</Typography>
		</Stack>
	);
};
